import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../lib/analytics";
import ParanoidiPricingCards from "../../pricing/ParanoidiPricingCards";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";

const LOGIN_HIGHLIGHT_KEYS = [
  "songRequests",
  "publicPage",
  "gigTools",
];

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/[ö]/g, "o")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function getApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined" ? window.PARANOIDI_API_BASE_URL : "";

  if (fromWindow) {
    return String(fromWindow).replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:4000/api";
  }

  return "/backend/api";
}

function showTransitionLoaderOnNextPage() {
  window.sessionStorage.setItem("paranoidi_transition_loader", "1");
}

function persistAuthSession(data) {
  const userValue = JSON.stringify(data.user);
  const performerValue = JSON.stringify(data.performformer);
  const slugValue = data.performer?.slug || "";
  const authTypeValue = data.auth?.type || "password";

  window.sessionStorage.setItem("paranoidi_auth_user", userValue);
  window.sessionStorage.setItem("paranoidi_auth_performer", performerValue);
  window.sessionStorage.setItem("paranoidi_performer_slug", slugValue);
  window.sessionStorage.setItem("paranoidi_auth_type", authTypeValue);

  window.localStorage.setItem("paranoidi_auth_user", userValue);
  window.localStorage.setItem("paranoidi_auth_performer", performerValue);
  window.localStorage.setItem("paranoidi_performer_slug", slugValue);
  window.localStorage.setItem("paranoidi_auth_type", authTypeValue);
}

const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9._-]{1,28}[a-z0-9])$/;

const RESERVED_USERNAMES = new Set([
  "admin",
  "api",
  "backend",
  "login",
  "logout",
  "register",
  "signup",
  "support",
  "help",
  "billing",
  "subscription",
  "paranoidi",
]);

function normalizeUsername(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "")
    .slice(0, 30);
}

function isSafePerformerName(value) {
  const name = String(value || "").trim();

  return (
    name.length >= 2 &&
    name.length <= 80 &&
    !/[<>\\{}[\]`]/.test(name)
  );
}

function isValidSignupEmail(value) {
  const email = String(value || "").trim().toLowerCase();

  return email.length <= 180 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidUsername(value) {
  const username = normalizeUsername(value);

  return USERNAME_PATTERN.test(username) && !RESERVED_USERNAMES.has(username);
}

export default function LoginPage({
  isLoggedIn = false,
  loggedInPerformer = null,
  loginName,
  setLoginName,
  loginPassword,
  setLoginPassword,
  loginError,
  isLoggingIn,
  handleLogin,
}) {
  const { t, i18n } = useTranslation("admin");
  const emailLanguage = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en"
    : "fi";
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [passwordResetMessage, setPasswordResetMessage] = useState("");
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);

  const [showSignupWizard, setShowSignupWizard] = useState(false);
  const [signupStep, setSignupStep] = useState(1);
  const [signupMessage, setSignupMessage] = useState("");
  const [signupPerformerName, setSignupPerformerName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupUsername, setSignupUsername] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupSlug, setSignupSlug] = useState("");
  const [signupSlugTouched, setSignupSlugTouched] = useState(false);
  const [signupPublicTitle, setSignupPublicTitle] = useState("");
  const [signupPlan, setSignupPlan] = useState("free");
  const [isCreatingSignup, setIsCreatingSignup] = useState(false);

  const loggedInPerformerName = loggedInPerformer?.name || "Paranoidi";

  useEffect(() => {
    const shouldLockScroll = showSignupWizard || showPasswordReset;

    if (!shouldLockScroll) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [showSignupWizard, showPasswordReset]);


  function scrollToLogin() {
    const loginSection = document.getElementById("kirjaudu");

    if (!loginSection) {
      return;
    }

    const targetTop =
      loginSection.getBoundingClientRect().top + window.scrollY - 220;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: "smooth",
    });
  }

  function openSignupWizard(source = "unknown") {
    trackEvent("signup_wizard_opened", {
      source,
    });

    setSignupStep(1);
    setSignupMessage("");
    setShowPasswordReset(false);
    setShowSignupWizard(true);
  }

  function closeSignupWizard() {
    setShowSignupWizard(false);
    setSignupMessage("");
  }

  function openPasswordReset() {
    setPasswordResetEmail(loginName || "");
    setPasswordResetMessage("");
    setShowSignupWizard(false);
    setShowPasswordReset(true);
  }

  function closePasswordReset() {
    setShowPasswordReset(false);
    setPasswordResetMessage("");
  }

  function handleSignupPerformerNameChange(value) {
    setSignupPerformerName(value);

    if (!signupSlugTouched) {
      setSignupSlug(slugify(value));
    }
  }

  async function handleSignupNext() {
    setSignupMessage("");

    if (signupStep === 1) {
      if (
        !signupPerformerName.trim() ||
        !signupEmail.trim() ||
        !signupUsername.trim() ||
        !signupPassword.trim()
      ) {
        setSignupMessage(t("loginPage.signup.validation.requiredFields"));
        return;
      }

      if (!isSafePerformerName(signupPerformerName)) {
        setSignupMessage(t("loginPage.signup.validation.performerNameInvalid"));
        return;
      }

      if (!isValidSignupEmail(signupEmail)) {
        setSignupMessage(t("loginPage.signup.validation.emailInvalid"));
        return;
      }

      if (!isValidUsername(signupUsername)) {
        setSignupMessage(t("loginPage.signup.validation.usernameInvalid"));
        return;
      }

      if (signupPassword.length < 8) {
        setSignupMessage(t("loginPage.signup.validation.passwordTooShort"));
        return;
      }

      if (!signupSlugTouched || !signupSlug.trim()) {
        setSignupSlug(slugify(signupPerformerName));
      }

      if (!signupPublicTitle.trim()) {
        setSignupPublicTitle(
          t("loginPage.signup.defaultPublicTitle", {
            performer: signupPerformerName.trim(),
          })
        );
      }

      setSignupStep(2);
      return;
    }

    if (signupStep === 2) {
      if (!signupSlug.trim()) {
        setSignupMessage(t("loginPage.signup.validation.slugRequired"));
        return;
      }

      setSignupStep(3);
      return;
    }

    setIsCreatingSignup(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/register`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          performerName: signupPerformerName.trim(),
          email: signupEmail.trim(),
          username: signupUsername.trim(),
          password: signupPassword,
          slug: signupSlug.trim(),
          publicTitle:
            signupPublicTitle.trim() ||
            t("loginPage.signup.defaultPublicTitle", {
              performer: signupPerformerName.trim(),
            }),
          plan: signupPlan,
          language: emailLanguage,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        const errorCode = data?.code || "";

        if (errorCode === "USER_ALREADY_EXISTS") {
          setSignupMessage(t("loginPage.signup.errors.userAlreadyExists"));
          return;
        }

        if (errorCode === "SLUG_ALREADY_EXISTS") {
          setSignupMessage(t("loginPage.signup.errors.slugAlreadyExists"));
          return;
        }

        if (errorCode === "DUPLICATE_VALUE") {
          setSignupMessage(t("loginPage.signup.errors.duplicateValue"));
          return;
        }

        setSignupMessage(t("loginPage.signup.errors.createFailed"));
        return;
      }

      persistAuthSession(data);

      trackEvent("signup_completed", {
        plan: signupPlan,
        performer_slug: data.performer?.slug || signupSlug.trim(),
      });

      if (["basic", "plus"].includes(String(signupPlan).toLowerCase())) {
        const checkoutResponse = await fetch(
          `${getApiBaseUrl()}/billing/create-checkout-session`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify({
              slug: data.performer?.slug || signupSlug.trim(),
              plan: signupPlan,
            }),
          }
        );

        const checkoutData = await checkoutResponse.json().catch(() => null);

        if (!checkoutResponse.ok || !checkoutData?.ok || !checkoutData?.url) {
          setSignupMessage(t("loginPage.signup.errors.checkoutOpenFailed"));
          return;
        }

        window.location.href = checkoutData.url;
        return;
      }

      showTransitionLoaderOnNextPage();

      window.setTimeout(() => {
        window.location.href = "/admin";
      }, 150);
    } catch (error) {
      setSignupMessage(t("loginPage.signup.errors.backendConnectionFailed"));
    } finally {
      setIsCreatingSignup(false);
    }
  }

  async function handlePasswordResetSubmit(event) {
    event.preventDefault();

    const email = passwordResetEmail.trim();

    if (!email) {
      setPasswordResetMessage(
        t("loginPage.passwordReset.validation.emailRequired")
      );
      return;
    }

    setIsSendingPasswordReset(true);
    setPasswordResetMessage("");

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/auth/request-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            email,
            language: emailLanguage,
          }),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        throw new Error(t("loginPage.passwordReset.errors.sendFailed"));
      }

      setPasswordResetMessage(t("loginPage.passwordReset.success"));
    } catch (error) {
      setPasswordResetMessage(t("loginPage.passwordReset.errors.sendFailed"));
    } finally {
      setIsSendingPasswordReset(false);
    }
  }

  const modalLayer =
    typeof document !== "undefined"
      ? createPortal(
          <>
            {showSignupWizard && (
              <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/85 p-4 backdrop-blur sm:p-6">
                <div
                  className={[
                    "paranoidi-modal-reveal relative max-h-[calc(100svh-2rem)] w-full max-w-[calc(100vw-2rem)] overflow-auto rounded-[1.5rem] border border-white/10 bg-zinc-950 p-4 text-white shadow-2xl shadow-black/70 sm:p-5",
                    signupStep === 3
                      ? "sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl"
                      : "sm:max-w-md lg:max-w-lg",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.22em] text-red-400">
                        {t("loginPage.signup.eyebrow")}
                      </p>

                      <h2 className="mt-3 text-2xl font-black uppercase tracking-[-0.04em] text-white sm:text-3xl">
                        {signupStep === 1 &&
                          t("loginPage.signup.steps.performer")}
                        {signupStep === 2 &&
                          t("loginPage.signup.steps.publicChannel")}
                        {signupStep === 3 &&
                          t("loginPage.signup.steps.plan")}
                      </h2>

                      <p className="mt-2 text-sm font-semibold leading-6 text-zinc-400">
                        {t("loginPage.signup.description")}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={closeSignupWizard}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-black text-white transition hover:bg-white/10"
                      aria-label={t("loginPage.common.close")}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`h-2 rounded-full ${
                          signupStep >= step ? "bg-red-500" : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>

                  <div className="mt-6">
                    {signupStep === 1 && (
                      <div className="grid gap-3">
                        <input
                          type="text"
                          name="paranoidi-performer-name"
                          value={signupPerformerName}
                          onChange={(event) =>
                            handleSignupPerformerNameChange(event.target.value)
                          }
                          autoComplete="off"
                          className="w-full rounded-xl border border-white/10 bg-white px-4 py-2.5 text-sm font-black text-black outline-none"
                          placeholder={t(
                            "loginPage.signup.fields.performerName"
                          )}
                        />

                        <input
                          type="email"
                          name="paranoidi-signup-email"
                          value={signupEmail}
                          onChange={(event) =>
                            setSignupEmail(event.target.value)
                          }
                          autoComplete="email"
                          className="w-full rounded-xl border border-white/10 bg-white px-4 py-2.5 text-sm font-black text-black outline-none"
                          placeholder={t("loginPage.common.email")}
                        />

                        <input
                          type="text"
                          name="paranoidi-signup-username"
                          value={signupUsername}
                          onChange={(event) =>
                            setSignupUsername(
                              normalizeUsername(event.target.value)
                            )
                          }
                          autoComplete="username"
                          className="w-full rounded-xl border border-white/10 bg-white px-4 py-2.5 text-sm font-black text-black outline-none"
                          placeholder={t("loginPage.signup.fields.username")}
                        />

                        <div className="flex w-full overflow-hidden rounded-2xl border border-white/10 bg-white">
                          <input
                            type={showSignupPassword ? "text" : "password"}
                            value={signupPassword}
                            onChange={(event) =>
                              setSignupPassword(event.target.value)
                            }
                            autoComplete="new-password"
                            className="min-w-0 flex-1 border-0 bg-white px-4 py-2.5 text-sm font-black text-black outline-none"
                            placeholder={t("loginPage.common.password")}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              setShowSignupPassword((value) => !value)
                            }
                            className="m-2 rounded-xl border border-black/25 px-3 text-xs font-black uppercase tracking-[0.08em] text-black transition duration-200 hover:bg-black hover:text-white active:scale-[0.96]"
                          >
                            {showSignupPassword
                              ? t("loginPage.common.hide")
                              : t("loginPage.common.show")}
                          </button>
                        </div>
                      </div>
                    )}

                    {signupStep === 2 && (
                      <div className="grid gap-3">
                        <label className="grid gap-2 text-sm font-black text-zinc-200">
                          {t("loginPage.signup.fields.publicLink")}

                          <div className="grid gap-2 rounded-2xl border border-white/10 bg-black p-3 text-sm font-bold text-zinc-400 sm:grid-cols-[auto_1fr] sm:items-center">
                            <span>/p/</span>

                            <input
                              type="text"
                              value={signupSlug}
                              onChange={(event) => {
                                setSignupSlugTouched(true);
                                setSignupSlug(slugify(event.target.value));
                              }}
                              className="min-w-0 rounded-xl border border-white/10 bg-white px-3 py-2 font-black text-black outline-none"
                              placeholder={t(
                                "loginPage.signup.fields.slugPlaceholder"
                              )}
                            />
                          </div>
                        </label>

                        <input
                          type="text"
                          value={signupPublicTitle}
                          onChange={(event) =>
                            setSignupPublicTitle(event.target.value)
                          }
                          className="w-full rounded-xl border border-white/10 bg-white px-4 py-2.5 text-sm font-black text-black outline-none"
                          placeholder={t(
                            "loginPage.signup.fields.publicTitle"
                          )}
                        />
                      </div>
                    )}

                    {signupStep === 3 && (
                      <div className="grid gap-4">
                        <ParanoidiPricingCards
                          selectedPlan={signupPlan}
                          onSelectPlan={setSignupPlan}
                          showExcluded={false}
                          compact
                        />

                        <p className="rounded-2xl border border-white/10 bg-black px-4 py-3 text-xs font-semibold leading-5 text-zinc-400">
                          {t("loginPage.signup.planInfo")}
                        </p>
                      </div>
                    )}

                    {signupMessage && (
                      <p className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-bold text-zinc-200">
                        {signupMessage}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={() =>
                        signupStep > 1
                          ? setSignupStep((step) => step - 1)
                          : closeSignupWizard()
                      }
                      className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-white/10"
                    >
                      {signupStep > 1
                        ? t("loginPage.common.back")
                        : t("loginPage.common.close")}
                    </button>

                    <button
                      type="button"
                      onClick={handleSignupNext}
                      disabled={isCreatingSignup}
                      className="inline-flex items-center justify-center gap-3 rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                    >
                      {isCreatingSignup && (
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                        />
                      )}

                      <span>
                        {signupStep < 3
                          ? t("loginPage.common.next")
                          : isCreatingSignup
                            ? t("loginPage.signup.actions.creating")
                            : ["basic", "plus"].includes(
                                  String(signupPlan).toLowerCase()
                                )
                              ? t("loginPage.signup.actions.continueStripe")
                              : t("loginPage.signup.actions.createChannel")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showPasswordReset && (
              <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/80 p-4 backdrop-blur">
                <div className="paranoidi-modal-reveal relative w-full max-w-md rounded-[2rem] border border-white/10 bg-zinc-950 p-5 text-white shadow-2xl shadow-black/70 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.22em] text-red-400">
                        {t("loginPage.passwordReset.eyebrow")}
                      </p>

                      <h2 className="mt-3 text-2xl font-black uppercase tracking-[-0.04em] text-white">
                        {t("loginPage.passwordReset.title")}
                      </h2>

                      <p className="mt-2 text-sm font-semibold leading-6 text-zinc-400">
                        {t("loginPage.passwordReset.description")}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={closePasswordReset}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-black text-white transition hover:bg-white/10"
                      aria-label={t("loginPage.common.close")}
                    >
                      ✕
                    </button>
                  </div>

                  <form
                    onSubmit={handlePasswordResetSubmit}
                    className="mt-6 grid gap-3"
                  >
                    <input
                      type="email"
                      value={passwordResetEmail}
                      onChange={(event) =>
                        setPasswordResetEmail(event.target.value)
                      }
                      className="w-full rounded-2xl border border-white/10 bg-white px-4 py-3 text-center text-sm font-black text-black outline-none transition focus:border-red-400"
                      placeholder={t("loginPage.common.emailAddress")}
                    />

                    {passwordResetMessage && (
                      <p className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-center text-sm font-bold text-zinc-200">
                        {passwordResetMessage}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={isSendingPasswordReset}
                      className="inline-flex items-center justify-center gap-3 rounded-2xl bg-red-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white transition duration-200 hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                    >
                      {isSendingPasswordReset && (
                        <span
                          aria-hidden="true"
                          className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                        />
                      )}

                      <span>
                        {isSendingPasswordReset
                          ? t("loginPage.passwordReset.actions.sending")
                          : t("loginPage.passwordReset.actions.sendLink")}
                      </span>
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>,
          document.body
        )
      : null;

  return (
    <>
      <style>
        {`
          @keyframes paranoidiLoginReveal {
            0% {
              opacity: 0;
              transform: translateY(24px) scale(0.985);
              filter: blur(10px);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes paranoidiLoginGlow {
            0%, 100% {
              opacity: 0.28;
              transform: scale(1);
            }

            50% {
              opacity: 0.58;
              transform: scale(1.07);
            }
          }

          @keyframes paranoidiLoginSweep {
            0% {
              transform: translateX(-120%) skewX(-18deg);
              opacity: 0;
            }

            18% {
              opacity: 0.7;
            }

            100% {
              transform: translateX(140%) skewX(-18deg);
              opacity: 0;
            }
          }

          @keyframes paranoidiModalReveal {
            0% {
              opacity: 0;
              transform: translateY(18px) scale(0.985);
              filter: blur(8px);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes paranoidiLoginNeonFlicker {
            0%,
            18%,
            22%,
            25%,
            53%,
            57%,
            100% {
              opacity: 1;
              box-shadow:
                0 0 10px rgba(248, 113, 113, 0.72),
                0 0 26px rgba(239, 68, 68, 0.42),
                inset 0 0 12px rgba(239, 68, 68, 0.12);
            }

            20%,
            24%,
            55% {
              opacity: 0.38;
              box-shadow:
                0 0 4px rgba(248, 113, 113, 0.35),
                0 0 10px rgba(239, 68, 68, 0.18);
            }
          }

          @keyframes paranoidiLoginArrowDrop {
            0%,
            100% {
              transform: translateY(-2px);
            }

            50% {
              transform: translateY(4px);
            }
          }

          .paranoidi-login-scroll-cue {
            animation: paranoidiLoginNeonFlicker 1.65s linear infinite;
          }

          .paranoidi-login-scroll-cue svg {
            animation: paranoidiLoginArrowDrop 1.1s ease-in-out infinite;
          }

          .paranoidi-login-reveal {
            animation: paranoidiLoginReveal 760ms cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .paranoidi-login-delay-1 {
            animation-delay: 120ms;
          }

          .paranoidi-login-delay-2 {
            animation-delay: 220ms;
          }

          .paranoidi-modal-reveal {
            animation: paranoidiModalReveal 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .paranoidi-login-language {
            gap: 0 !important;
          }

          .paranoidi-login-language > span:first-child {
            display: none;
          }

          @media (prefers-reduced-motion: reduce) {
            .paranoidi-login-scroll-cue,
            .paranoidi-login-scroll-cue svg {
              animation: none !important;
            }
          }
        `}
      </style>

      <main className="relative min-h-screen overflow-hidden bg-[#040404] text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(239,68,68,0.30),transparent_30rem),radial-gradient(circle_at_18%_80%,rgba(255,255,255,0.07),transparent_24rem),linear-gradient(135deg,rgba(127,29,29,0.24),transparent_48%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.024)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.024)_1px,transparent_1px)] bg-[size:44px_44px]" />
          <div className="absolute left-1/2 top-8 h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl [animation:paranoidiLoginGlow_5.5s_ease-in-out_infinite]" />
          <div className="absolute left-0 top-[34%] h-20 w-1/2 bg-gradient-to-r from-transparent via-red-400/18 to-transparent blur-xl [animation:paranoidiLoginSweep_4.8s_ease-in-out_1s_infinite]" />
        </div>

        <button
          type="button"
          onClick={scrollToLogin}
          aria-label={t("loginPage.login.title")}
          className="paranoidi-login-scroll-cue absolute left-[calc(50%+4px)] z-50 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full border border-red-300/70 bg-black/85 text-red-100 backdrop-blur-md transition hover:border-red-200 hover:bg-red-500/20 active:scale-95 lg:hidden"
          style={{ top: "calc(100svh - 9rem)" }}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-8 w-8 drop-shadow-[0_0_14px_rgba(248,113,113,1)]"
            fill="none"
          >
            <path
              d="M5 8.5 12 15.5 19 8.5"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <section className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-5 py-6 sm:px-8 lg:px-10">
          <header className="paranoidi-login-reveal flex items-center justify-between gap-2 sm:gap-4">
            <a
              href={emailLanguage === "en" ? "/en/" : "/"}
              aria-label={t("loginPage.header.homeAria")}
              className="inline-flex items-center gap-2 no-underline sm:gap-3"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-red-400/20 bg-red-500/10 shadow-[0_0_18px_rgba(239,68,68,0.18)]">
                <img
                  src="/brand/scroll-to-top-icon.png"
                  alt=""
                  className="h-8 w-8 object-contain"
                  aria-hidden="true"
                />
              </span>

              <span className="text-base font-black uppercase tracking-[-0.04em] text-white sm:text-lg">
                Paranoidi
              </span>
            </a>

            <div className="theme-dark flex items-center gap-1 sm:gap-2">
              <LanguageSwitcher
                variant="public"
                className="paranoidi-login-language !mt-0"
              />

              <a
                href={emailLanguage === "en" ? "/en/" : "/"}
                className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-[0.7rem] font-black uppercase tracking-[0.12em] sm:px-4 sm:text-xs sm:tracking-[0.16em] text-zinc-300 no-underline transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-white"
              >
                {t("loginPage.header.home")}
              </a>
            </div>
          </header>

          <div className="grid flex-1 items-center gap-10 py-10 lg:grid-cols-[1fr_0.82fr] lg:py-12">
            <section className="paranoidi-login-reveal paranoidi-login-delay-1 relative min-h-[100svh] overflow-visible pb-48 text-center sm:pb-56 lg:min-h-0 lg:pb-0 lg:text-left">
              <p className="relative z-10 inline-flex items-center gap-3 rounded-full border border-red-400/20 bg-red-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-100">
                <span className="h-2 w-2 rounded-full bg-red-400 shadow-[0_0_18px_rgba(248,113,113,0.9)]" />
                {t("loginPage.hero.eyebrow")}
              </p>

              <h1 className="relative z-10 mt-6 text-[2.85rem] font-black uppercase leading-[0.84] tracking-[-0.08em] text-white sm:text-6xl lg:text-8xl">
                {t("loginPage.hero.titleLine1")}
                <br />
                {t("loginPage.hero.titleLine2")}
              </h1>

              <div className="relative z-10 mx-auto mt-8 grid max-w-xl gap-3 sm:grid-cols-3 lg:mx-0">
                {LOGIN_HIGHLIGHT_KEYS.map((key) => (
                  <div
                    key={key}
                    className="rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-xs font-black uppercase leading-5 tracking-[0.08em] text-zinc-200"
                  >
                    {t(`loginPage.highlights.${key}`)}
                  </div>
                ))}
              </div>
            </section>

            <section
              id="kirjaudu"
              className="paranoidi-login-reveal paranoidi-login-delay-2 lg:pt-44"
            >
              <div className="mx-auto w-full max-w-md">
                <form
                  onSubmit={handleLogin}
                  className="relative overflow-hidden rounded-[2rem] bg-zinc-950/94 p-5 shadow-2xl shadow-black/60 sm:p-6"
                >
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.18),transparent_42%)]" />

                  <div className="relative">
                    <h2 className="text-center text-3xl font-black uppercase tracking-[-0.05em] text-white">
                      {isLoggedIn
                        ? t("loginPage.login.loggedInTitle")
                        : t("loginPage.login.title")}
                    </h2>

                    <p className="mt-3 text-center text-sm font-semibold leading-6 text-zinc-100">
                      {isLoggedIn
                        ? t("loginPage.login.loggedInDescription", {
                            performer: loggedInPerformerName,
                          })
                        : t("loginPage.login.description")}
                    </p>

                    <div className="mt-6 grid gap-3">
                      {isLoggedIn ? (
                        <>
                          <a
                            href="/admin"
                            className="inline-flex items-center justify-center rounded-2xl bg-red-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white no-underline transition duration-200 hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98]"
                          >
                            {t("loginPage.login.returnToAdmin")}
                          </a>

                          <p className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-center text-sm font-bold text-zinc-300">
                            {t("loginPage.login.alreadyLoggedIn")}
                          </p>
                        </>
                      ) : (
                        <>
                          <label className="sr-only" htmlFor="login-name">
                            {t("loginPage.login.usernameOrEmail")}
                          </label>

                          <input
                            id="login-name"
                            type="text"
                            value={loginName}
                            onChange={(event) =>
                              setLoginName(event.target.value)
                            }
                            autoComplete="username"
                            className="w-full rounded-2xl border border-white/10 bg-white px-4 py-3 text-center text-sm font-black text-black outline-none transition placeholder:text-zinc-500 focus:border-red-400"
                            placeholder={t(
                              "loginPage.login.usernameOrEmail"
                            )}
                          />

                          <label
                            className="sr-only"
                            htmlFor="login-password"
                          >
                            {t("loginPage.common.password")}
                          </label>

                          <div className="flex w-full overflow-hidden rounded-2xl border border-white/10 bg-white">
                            <input
                              id="login-password"
                              type={showPassword ? "text" : "password"}
                              value={loginPassword}
                              onChange={(event) =>
                                setLoginPassword(event.target.value)
                              }
                              autoComplete="current-password"
                              className="min-w-0 flex-1 border-0 bg-white px-4 py-3 text-center text-sm font-black text-black outline-none"
                              placeholder={t("loginPage.common.password")}
                            />

                            <button
                              type="button"
                              onClick={() =>
                                setShowPassword((value) => !value)
                              }
                              className="m-2 rounded-xl border border-black/25 px-3 text-xs font-black uppercase tracking-[0.08em] text-black transition duration-200 hover:bg-black hover:text-white active:scale-[0.96]"
                            >
                              {showPassword
                                ? t("loginPage.common.hide")
                                : t("loginPage.common.show")}
                            </button>
                          </div>

                          {loginError && (
                            <p className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center text-sm font-bold text-red-200">
                              {loginError}
                            </p>
                          )}

                          <button
                            type="submit"
                            disabled={isLoggingIn}
                            className="inline-flex items-center justify-center gap-3 rounded-2xl bg-red-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white transition duration-200 hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                          >
                            {isLoggingIn && (
                              <span
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                              />
                            )}

                            <span>
                              {isLoggingIn
                                ? t("loginPage.login.loggingIn")
                                : t("loginPage.login.submit")}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              openSignupWizard("login_card_signup")
                            }
                            className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white transition duration-200 hover:-translate-y-0.5 hover:bg-white/10 active:scale-[0.98]"
                          >
                            {t("loginPage.login.createAccount")}
                          </button>

                          <button
                            type="button"
                            onClick={openPasswordReset}
                            className="text-sm font-black text-zinc-400 transition duration-200 hover:text-white active:scale-[0.98]"
                          >
                            {t("loginPage.login.forgotPassword")}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </section>
          </div>
        </section>
      </main>

      {modalLayer}
    </>
  );
}